/*******************************************************************************************
 * Responsibility - Handles reading and updating the for the production settings of a user.
 * *****************************************************************************************
 *
 * @format */

import {SavingChangesIndicator} from '../contact/contact';
import {Drawer, DrawerHeader} from '@sttark/design-system';
import {Checkbox, Button} from '@sttark/design-system';
import {InputWithErrorHandler} from 'frontier-form';
import React, {useState} from 'react';

export const ProductionPresets = props => {
    const [lastUpdatedField, setLastUpdatedField] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);

    return (
        <>
            <section className="account-preferences-section-10mc bg-structure-lightest">
                <Drawer isOpen={openDrawer} onClose={() => setOpenDrawer(false)}>
                    <DrawerHeader
                        title="How to use Label Roll Settings"
                        onCancel={() => setOpenDrawer(false)}
                        cancelTitle={'Close'}
                    />
                    <section>
                        <p className={'mt-0 mb-5'}>
                            Our label quoter empowers users with precise control over their roll
                            label specifications. Here's a breakdown of the customizable options and
                            their practical applications:
                        </p>

                        <p className={'mb-3'}>
                            <b>Fewest Rolls</b>
                        </p>
                        <ul className="mb-5" style={{listStyleType: 'disc', marginLeft: '20px'}}>
                            <li style={{listStyleType: 'disc', marginLeft: '20px'}}>
                                Condense multiple rolls of the same design into larger rolls.
                            </li>
                            <li style={{listStyleType: 'disc', marginLeft: '20px'}}>
                                Less to keep up with and / or less storage space.
                            </li>
                            <li style={{listStyleType: 'disc', marginLeft: '20px'}}>
                                Reducing set up time and waste from multiple rolls when machine
                                applying.
                            </li>
                        </ul>

                        <p className={'mb-3'}>
                            <b>Fewest Rolls</b>
                        </p>
                        <ul className="mb-5" style={{listStyleType: 'disc', marginLeft: '20px'}}>
                            <li style={{listStyleType: 'disc', marginLeft: '20px'}}>
                                Keep roll small enough to fit machine applicator.
                            </li>
                            <li style={{listStyleType: 'disc', marginLeft: '20px'}}>
                                Keep roll small enough to easily wield by hand.
                            </li>
                        </ul>

                        <p className={'mb-3'}>
                            <b>Min size</b>
                        </p>
                        <ul className="mb-5" style={{listStyleType: 'disc', marginLeft: '20px'}}>
                            <li style={{listStyleType: 'disc', marginLeft: '20px'}}>
                                Larger rolls to reduce set up time from smaller rolls when machine
                                applying.
                            </li>
                        </ul>
                    </section>
                </Drawer>
                <div className="account-preferences-section-10mc__row row-10mc--title">
                    <h3 className={'account-preferences-section-10mc__heading m-0'}>
                        Roll Settings
                    </h3>
                    <Button
                        className="account-preferences-section-10mc__roll_max_instructions"
                        label={<b>How do I use Roll Settings?</b>}
                        onClick={() => setOpenDrawer(true)}
                        secondary
                        small
                    />
                    <button
                        className="account-preferences-section-10mc__roll_max_instructions--mobile"
                        onClick={() => setOpenDrawer(true)}>
                        <u>How do I use Roll Settings?</u>
                    </button>
                </div>
                <div className="account-preferences-section-10mc__row">
                    <div className="form-group w-100">
                        <label>
                            <b>Max labels per roll (+0% / -10%)</b>
                        </label>
                        <InputWithErrorHandler
                            onChange={value =>
                                props.handleRollChange('formData.roll_qty_max', value)
                            }
                            onKeyDown={() => setLastUpdatedField('roll_qty_max')}
                            disabled={!!props.formData.roll_diameter_max}
                            value={props.formData.roll_qty_max || ''}
                            placeholder="Enter max labels per roll"
                            error={props.accountDataError}
                            className="form-control w-100"
                            errorPath="roll_qty_max"
                            label={' '}
                            newestds
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            isLoading={props.savingData}
                            fieldName="roll_qty_max"
                        />
                    </div>
                </div>

                <div className="account-preferences-section-10mc__row">
                    <div className="form-group w-100">
                        <label>
                            <b>Max roll diameter (+0% / -10%)</b>
                        </label>
                        <InputWithErrorHandler
                            onChange={value =>
                                props.handleRollChange('formData.roll_diameter_max', value)
                            }
                            onKeyDown={() => setLastUpdatedField('roll_diameter_max')}
                            value={props.formData.roll_diameter_max || ''}
                            placeholder="Enter max diameter per roll"
                            disabled={!!props.formData.roll_qty_max}
                            error={props.accountDataError}
                            className="form-control w-100"
                            errorPath="roll_diameter_max"
                            label={' '}
                            newestds
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            fieldName="roll_diameter_max"
                            isLoading={props.savingData}
                        />
                    </div>
                </div>

                <div className="account-preferences-section-10mc__row mb-0">
                    <div className="form-group w-100 checkbox-with-error-handler w-100">
                        <Checkbox
                            onClick={() => setLastUpdatedField('is_fewest_possible_rolls')}
                            checked={props.formData.is_fewest_possible_rolls || false}
                            value={props.formData.is_fewest_possible_rolls || false}
                            bold={props.formData.is_fewest_possible_rolls || false}
                            onChange={props.onCheckboxChange.bind(
                                this,
                                'formData.is_fewest_possible_rolls'
                            )}
                            error={props.error}>
                            Produce fewest possible rolls
                        </Checkbox>
                        <SavingChangesIndicator
                            fieldName="is_fewest_possible_rolls"
                            lastUpdatedField={lastUpdatedField}
                            isLoading={props.savingData}
                        />
                    </div>
                </div>
            </section>
            <section className="account-preferences-section-10mc bg-structure-lightest">
                <div className="account-preferences-section-10mc__row row-10mc--title">
                    <h3 className={'account-preferences-section-10mc__heading m-0'}>
                        Substrate Backing
                    </h3>
                </div>

                <div className="account-preferences-section-10mc__row">
                    <div className="form-group w-100">
                        <Checkbox
                            onClick={() => setLastUpdatedField('is_using_paper_backing')}
                            checked={props.formData.is_using_paper_backing || false}
                            value={props.formData.is_using_paper_backing || false}
                            bold={props.formData.is_using_paper_backing || false}
                            onChange={props.onCheckboxChange.bind(
                                this,
                                'formData.is_using_paper_backing'
                            )}
                            error={props.error}>
                            Use Paper Backing when possible
                        </Checkbox>
                        <SavingChangesIndicator
                            fieldName="is_using_paper_backing"
                            lastUpdatedField={lastUpdatedField}
                            isLoading={props.savingData}
                        />
                    </div>
                </div>
            </section>
        </>
    );
};
